import React from "react";
import { motion, animate } from "motion/react";

// Scroll-reveal wrapper — fade + rise once, on first view. Used across
// section-heavy landing pages for a consistent "scroll reveal" motion
// language. Opacity intentionally never drops below 1. An earlier version
// animated opacity 0→1 on scroll-into-view, gated on IntersectionObserver
// timing — on real content-critical sections that meant whole blocks could
// sit invisible if the observer fired late (confirmed: several sections
// rendered fully blank in testing). Content must never depend on JS/
// animation timing to be visible, so this only animates a small vertical
// settle; worst case (JS never fires) the content is still fully visible,
// just static.
export const Reveal = ({ children, className = "", delay = 0 }) => (
    <motion.div
        initial={{ y: 16 }}
        whileInView={{ y: 0 }}
        viewport={{ once: true, amount: 0, margin: "0px 0px 400px 0px" }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1], delay }}
        className={className}
    >
        {children}
    </motion.div>
);

export const Eyebrow = ({ children, tone = "blue" }) => {
    const tones = {
        blue: "bg-blue-50 text-blue-600",
        red: "bg-red-50 text-red-600",
        slate: "bg-slate-100 text-slate-600",
        amber: "bg-amber-50 text-amber-600",
    };
    return (
        <span className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs sm:text-sm font-black ${tones[tone]}`}>
            {children}
        </span>
    );
};

// Animates on mount rather than gating on viewport-enter — sidesteps the
// same invisible-content risk documented on `Reveal` above (an earlier
// viewport-triggered version left every counter stuck at 0: `onViewportEnter`
// was wired to a plain `<span>`, which silently ignores framer-motion-only
// props). For below-the-fold placements the count-up simply resolves before
// a visitor scrolls to it, which reads fine.
export const CounterInView = ({ to, duration = 2, suffix = "", prefix = "" }) => {
    const [count, setCount] = React.useState(0);

    React.useEffect(() => {
        const controls = animate(0, to, {
            duration,
            onUpdate(value) {
                setCount(Math.round(value));
            },
        });
        return () => controls.stop();
    }, [to, duration]);

    return (
        <>
            {prefix}
            {count}
            {suffix}
        </>
    );
};
